/**
 * Input parsing/validation for the Gather & Import console — pure functions,
 * extracted from the page component so they can be exercised without
 * rendering. The server (aggregate.ts validateSubmission) remains the hard
 * gate; this layer gives the agent/user fast, specific feedback in the UI.
 */

export const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Coerce a value to a finite number, tolerating "$1,234.56" strings. */
export function toNum(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const t = v.trim();
    if (!t) return null;
    const n = Number(t.replace(/[$,\s]/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export interface EmpowerBatch {
  balances: Record<string, unknown>[];
  txns: Record<string, unknown>[];
  /** sourceIds to force past the overwrite guard (locked / settled rows). */
  overrideIds: string[];
  /** Split a stored txn into parts: `{sourceId, parts:[{amount,category,…}]}`. */
  splits: unknown[];
  /** sourceIds of split parents to reverse. */
  unsplits: string[];
  error: string | null;
}

const emptyBatch = (error: string | null): EmpowerBatch => ({
  balances: [],
  txns: [],
  overrideIds: [],
  splits: [],
  unsplits: [],
  error,
});

/**
 * Parse the ONE Empower box: a combined `{balances, transactions}` object (a
 * bare array is accepted as balances-only). Balance rows carry full identity —
 * `{date, institution, owner, type, account, balance}`, liabilities negative —
 * because a single box feeds many accounts. Transactions are passed through
 * (server validates `status`); each needs its own `account`. The object may
 * also carry `overrideIds`, `splits`, and `unsplits` (passed straight through;
 * the server validates them).
 */
export function parseEmpower(raw: string): EmpowerBatch {
  const text = raw.trim();
  if (!text) return emptyBatch(null);

  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return emptyBatch("invalid JSON");
  }

  let balArr: unknown = [];
  let txArr: unknown = [];
  let overrideArr: unknown = [];
  let splitsArr: unknown = [];
  let unsplitsArr: unknown = [];
  if (Array.isArray(data)) {
    balArr = data;
  } else if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    balArr = o.balances ?? [];
    txArr = o.transactions ?? [];
    overrideArr = o.overrideIds ?? [];
    splitsArr = o.splits ?? [];
    unsplitsArr = o.unsplits ?? [];
  } else {
    return emptyBatch("expected an object with `balances` and `transactions`");
  }
  if (!Array.isArray(balArr)) return emptyBatch("`balances` must be an array");
  if (!Array.isArray(txArr)) return emptyBatch("`transactions` must be an array");
  if (!Array.isArray(overrideArr)) return emptyBatch("`overrideIds` must be an array");
  if (!Array.isArray(splitsArr)) return emptyBatch("`splits` must be an array");
  if (!Array.isArray(unsplitsArr)) return emptyBatch("`unsplits` must be an array");
  const overrideIds = overrideArr.map((s) => String(s).trim()).filter(Boolean);
  const unsplits = unsplitsArr.map((s) => String(s).trim()).filter(Boolean);

  const balances: Record<string, unknown>[] = [];
  for (const row of balArr) {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      return emptyBatch(`bad balance row: ${JSON.stringify(row)}`);
    }
    const o = row as Record<string, unknown>;
    const d = typeof o.date === "string" ? o.date.trim() : "";
    const acct = typeof o.account === "string" ? o.account.trim() : "";
    const b = toNum(o.balance);
    if (!ISO_DATE.test(d) || !acct || b === null) {
      return emptyBatch(`balance row needs date, account, balance: ${JSON.stringify(row)}`);
    }
    balances.push(o);
  }

  const txns: Record<string, unknown>[] = [];
  for (const row of txArr) {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      return emptyBatch("each transaction must be an object");
    }
    txns.push(row as Record<string, unknown>);
  }

  return { balances, txns, overrideIds, splits: splitsArr, unsplits, error: null };
}

/** Stable key for a manual account's balance box (also the localStorage key). */
export function balKey(p: { institution: string; owner: string; account: string }): string {
  return `${p.institution}|${p.owner}|${p.account}`;
}

/**
 * Assemble the whole batch to POST: Empower's balances + the manual single
 * values (identity stamped, at the shared as-of date), plus Empower's
 * transactions. Returns the rows plus a list of inputs that need fixing.
 */
export function assembleSubmission(opts: {
  empower: EmpowerBatch;
  manualItems: { source: string; institution: string; owner: string; type: string; account: string }[];
  balInputs: Record<string, string>;
  asOf: string;
}): { balances: unknown[]; transactions: unknown[]; overrideIds: string[]; splits: unknown[]; unsplits: string[]; bad: string[] } {
  const { empower, manualItems, balInputs, asOf } = opts;
  const bad: string[] = [];
  const balances: unknown[] = [];

  if (empower.error) bad.push(`Empower box (${empower.error})`);
  else balances.push(...empower.balances);

  for (const p of manualItems) {
    if (p.source === "empower") continue;
    const raw = (balInputs[balKey(p)] ?? "").trim();
    if (!raw) continue;
    const n = toNum(raw);
    if (n === null) {
      bad.push(`${p.account} (not a number)`);
      continue;
    }
    balances.push({
      date: asOf,
      institution: p.institution,
      owner: p.owner,
      type: p.type,
      account: p.account,
      balance: n,
    });
  }

  return {
    balances,
    transactions: empower.error ? [] : empower.txns,
    overrideIds: empower.error ? [] : empower.overrideIds,
    splits: empower.error ? [] : empower.splits,
    unsplits: empower.error ? [] : empower.unsplits,
    bad,
  };
}
